import React, { useContext } from "react";
import { 
    Card, 
    CardContent, 
    CardHeader, 
    Typography, 
    Box, 
    Button, 
    IconButton,
    Divider,
    Switch,
    FormControlLabel,
    Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import YAML from "yaml";
import { WamlContext } from "../WAMLContent";
import PercentInput from "./PercentInput";
import SecondsInput from "./SecondsInput";
import { createDefaultLatencyObjective, createDefaultAvailabilityObjective } from "./constants";

const useStyles = makeStyles({
    card: {
        marginBottom: '16px',
        border: '1px solid #e0e0e0'
    },
    cardHeader: {
        backgroundColor: '#f5f5f5',
        '& .MuiCardHeader-title': {
            fontSize: '1.1rem',
            fontWeight: 'bold'
        }
    },
    objectiveRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
        flexWrap: 'wrap'
    },
    latencyRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        padding: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '4px'
    },
    addButton: {
        marginTop: '8px'
    }
});

export interface SLOProtocolCardProps {
    protocol: 'http' | 'grpc';
    deployKey: string;
    wapp: any;
}

const SLOProtocolCard = ({ protocol, deployKey, wapp }: SLOProtocolCardProps) => {
    const classes = useStyles();
    const context = useContext(WamlContext);

    const protocolSpec = wapp?.deploy?.[deployKey]?.slos?.[protocol];
    const isEnabled = !!protocolSpec;

    const updateWaml = (updatedSpec: any) => {
        const newWapp = JSON.parse(JSON.stringify(wapp));
        
        if (!newWapp.deploy) newWapp.deploy = {};
        if (!newWapp.deploy[deployKey]) newWapp.deploy[deployKey] = {};
        if (!newWapp.deploy[deployKey].slos) newWapp.deploy[deployKey].slos = {};
        
        if (updatedSpec) {
            newWapp.deploy[deployKey].slos[protocol] = updatedSpec;
        } else {
            delete newWapp.deploy[deployKey].slos[protocol];
            if (Object.keys(newWapp.deploy[deployKey].slos).length === 0) {
                delete newWapp.deploy[deployKey].slos;
            }
        }

        context.modifyWaml(YAML.stringify(newWapp), { tab: "SLOs" });
    };

    const handleToggle = (enabled: boolean) => {
        if (enabled) {
            updateWaml({});
        } else {
            updateWaml(null);
        }
    };

    const handleAvailabilityChange = (e: any) => {
        const newSpec = { ...protocolSpec };
        if (!newSpec.availability) newSpec.availability = {};
        newSpec.availability.percent = e.target.value;
        updateWaml(newSpec);
    };

    const handleLatencyChange = (index: number, field: 'percent' | 'seconds', value: string) => {
        const newSpec = { ...protocolSpec };
        if (!newSpec.latency) newSpec.latency = [];
        if (!newSpec.latency[index]) newSpec.latency[index] = {};
        newSpec.latency[index][field] = value;
        updateWaml(newSpec);
    };

    const addLatencyObjective = () => {
        const newSpec = { ...protocolSpec };
        if (!newSpec.latency) newSpec.latency = [];
        newSpec.latency.push(createDefaultLatencyObjective());
        updateWaml(newSpec);
    };

    const removeLatencyObjective = (index: number) => {
        const newSpec = { ...protocolSpec };
        if (newSpec.latency) {
            newSpec.latency.splice(index, 1);
            updateWaml(newSpec);
        }
    };

    const toggleAvailability = (enabled: boolean) => {
        const newSpec = { ...protocolSpec };
        if (enabled) {
            newSpec.availability = createDefaultAvailabilityObjective();
        } else {
            delete newSpec.availability;
        }
        updateWaml(newSpec);
    };

    return (
        <Card className={classes.card}>
            <CardHeader
                className={classes.cardHeader}
                title={
                    <Box display="flex" alignItems="center">
                        {`${protocol.toUpperCase()} SLOs`}
                        <Tooltip title={`Configure Service Level Objectives for ${protocol.toUpperCase()} requests. SLOs are automatically converted to dashboards and alerts when deployed.`}>
                            <InfoIcon style={{ marginLeft: '8px', fontSize: '1rem', color: '#666' }} />
                        </Tooltip>
                    </Box>
                }
                action={
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isEnabled}
                                onChange={(e) => handleToggle(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Enabled"
                    />
                }
            />
            {isEnabled && (
                <CardContent>
                    {/* Availability Section */}
                    <Box mb={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!protocolSpec?.availability}
                                    onChange={(e) => toggleAvailability(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={<Typography variant="subtitle2">Availability Objective</Typography>}
                        />
                        {protocolSpec?.availability && (
                            <Box className={classes.objectiveRow} mt={2}>
                                <PercentInput
                                    name={`${protocol}.availability.percent`}
                                    value={protocolSpec.availability.percent}
                                    onChange={handleAvailabilityChange}
                                />
                                <Typography>of {protocol.toUpperCase()} requests will complete without error</Typography>
                            </Box>
                        )}
                    </Box>

                    <Divider />

                    {/* Latency Section */}
                    <Box mt={3}>
                        <Typography variant="subtitle2" gutterBottom>
                            Latency Objectives
                        </Typography>
                        {protocolSpec?.latency?.map((latency: any, index: number) => (
                            <Box key={index} className={classes.latencyRow}>
                                <PercentInput
                                    name={`${protocol}.latency.${index}.percent`}
                                    value={latency.percent}
                                    onChange={(e) => handleLatencyChange(index, 'percent', e.target.value)}
                                />
                                <Typography>of {protocol.toUpperCase()} requests will complete in</Typography>
                                <SecondsInput
                                    name={`${protocol}.latency.${index}.seconds`}
                                    value={latency.seconds}
                                    onChange={(e) => handleLatencyChange(index, 'seconds', e.target.value)}
                                />
                                <Typography>
                                    {latency.seconds === "1" ? "second or less" : "seconds or less"}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => removeLatencyObjective(index)}
                                    color="secondary"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addLatencyObjective}
                            className={classes.addButton}
                            variant="outlined"
                            size="small"
                        >
                            Add Latency Objective
                        </Button>
                    </Box>
                </CardContent>
            )}
        </Card>
    );
};

export default SLOProtocolCard;
